/* eslint-disable jsdoc/require-jsdoc */
import React, {
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef
} from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import {
  DeviceSummaryAttribute,
  ParametersType,
  ParameterAttribute,
  ParticleData,
  DeviceAttributes,
  Group,
  DashboardDataFilter
} from '../constants/telosair-interfaces';
import {
  getParametersList,
  getGroupSummary,
  getDeviceSummary,
  getDataSummary,
  validatePublicToken,
  getAllDevices,
  getAllGroups
} from '../services/public-api.service';
import { ALL_PARAMETERS } from '../constants/global-constants';
import { Filters, setAppliedFilter } from '../services/localstorage.service';

type PublicDataContextProps = {
  loading: boolean;
  isAuthorized: boolean;
  errorMessage: string;
  parameters: ParameterAttribute[];
  devices: DeviceAttributes[];
  groupedData: Group[];
  deviceDataSummary: { [key: string]: DeviceSummaryAttribute };
  groupDataSummary: { [key: string]: DeviceSummaryAttribute };
  dataSummary: ParticleData;
  defaultDashboardFilter: DashboardDataFilter | null;
  fetchDeviceSummary: (startDate: number, endDate: number) => Promise<void>;
  fetchGroupSummary: (startDate: number, endDate: number) => Promise<void>;
  fetchSummary: (startDate: number, endDate: number) => Promise<void>;
  setDataSummary: (data: ParticleData) => void;
  setGroupDataSummary: (data: { [key: string]: DeviceSummaryAttribute }) => void;
};

const PublicDataContext = createContext({} as PublicDataContextProps);

export function PublicDataProvider(): ReactElement {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [parameters, setParameters] = useState<ParameterAttribute[]>([]);
  const [devices, setDevices] = useState<DeviceAttributes[]>([]);
  const [groupedData, setGroupedData] = useState<Group[]>([]);
  const [deviceDataSummary, setDeviceDataSummary] = useState<{
    [key: string]: DeviceSummaryAttribute;
  }>({});
  const [dataSummary, setDataSummary] = useState<ParticleData>({} as ParticleData);
  const [groupDataSummary, setGroupDataSummary] = useState<{
    [key: string]: DeviceSummaryAttribute;
  }>({});
  const [defaultDashboardFilter, setDefaultDashboardFilter] = useState<DashboardDataFilter | null>(
    null
  );
  const isValidatingRef = useRef(false);

  const fetchDeviceSummary = useCallback(
    async (startDate: number, endDate: number): Promise<void> => {
      try {
        const deviceDataResponse = await getDeviceSummary({
          startDate,
          endDate
        });
        const summary: { [key: string]: DeviceSummaryAttribute } = deviceDataResponse.data;
        if (Object.keys(summary).length > 0) {
          setDeviceDataSummary(summary);
        } else {
          setDeviceDataSummary({});
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    },
    []
  );

  const fetchGroupSummary = useCallback(
    async (startDate: number, endDate: number): Promise<void> => {
      try {
        const groupDataResponse = await getGroupSummary({
          startDate,
          endDate
        });
        const summary: { [key: string]: DeviceSummaryAttribute } = groupDataResponse.data;
        if (Object.keys(summary).length > 0) {
          setGroupDataSummary(summary);
        } else {
          setGroupDataSummary({});
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    },
    []
  );

  const fetchDataSummary = useCallback(
    async (startDate: number, endDate: number): Promise<void> => {
      try {
        const dataResponse = await getDataSummary({
          startDate,
          endDate
        });
        if (dataResponse.data.parameters) {
          setDataSummary(dataResponse.data.parameters);
        } else {
          setDataSummary({} as ParticleData);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    },
    []
  );

  const fetchParameterList = useCallback(async (): Promise<void> => {
    try {
      const response = await getParametersList();
      const list: ParameterAttribute[] = [];

      // Get default user config from sessionStorage (set by public dashboard page)
      const defaultConfigStr = sessionStorage.getItem('defaultUserConfig');
      const defaultConfig = defaultConfigStr ? JSON.parse(defaultConfigStr) : {};

      response?.data.forEach(
        (item: { parameterName: ParametersType; displayName: string; id: number }) => {
          const parameter = ALL_PARAMETERS[item.parameterName];
          list.push({
            ...parameter,
            unit: (parameter && defaultConfig[parameter.unitKey]) || '',
            ...item
          });
        }
      );
      // Parameters the client switched off in settings are not shown publicly
      // either. There is no settings screen here, so they are dropped on fetch.
      const sortedList = list
        .filter((parameter) => parameter.isEnabled !== false)
        .sort((a, b) => a.order - b.order);
      setParameters(sortedList);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  }, []);

  const fetchAllDevices = useCallback(async (): Promise<DeviceAttributes[]> => {
    try {
      const response = await getAllDevices();
      const deviceList = response.data || [];
      setDevices(deviceList);
      return deviceList;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
    return [];
  }, []);

  const fetchAllGroups = useCallback(async (): Promise<Group[]> => {
    try {
      const response = await getAllGroups();
      const groupList = response.data || [];
      setGroupedData([...groupList]);
      return groupList;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
    return [];
  }, []);

  const fetchSummary = useCallback(
    async (startDate: number, endDate: number): Promise<void> => {
      try {
        await Promise.all([
          fetchDeviceSummary(startDate, endDate),
          fetchGroupSummary(startDate, endDate),
          fetchDataSummary(startDate, endDate)
        ]);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    },
    [fetchDeviceSummary, fetchGroupSummary, fetchDataSummary]
  );

  const validateAndSetupAccess = useCallback(
    async (token: string): Promise<void> => {
      // Prevent multiple simultaneous validation calls
      if (isValidatingRef.current) {
        return;
      }

      isValidatingRef.current = true;

      try {
        // Store token in sessionStorage first so the API can use it
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('isPublicView', 'true');

        // Validate the token using the API
        const response = await validatePublicToken();

        // If validation is successful, setup the session
        if (response.data) {
          sessionStorage.setItem('user_name', 'Public User');
          sessionStorage.setItem('client_name', 'Public View');

          // Set default user config with units for public view
          const defaultUserConfig = {
            unit: {
              co2: 'ppm',
              hum: '%',
              temp: '℃',
              tvoc: 'ppb',
              pm25_m: 'ug/m^3',
              pn03_m: '#/L\n',
              pm100_m: 'ug/m^3',
              pressure: '"H2O'
            },
            aqi_max: 100,
            aqi_min: 0,
            co2_max: 5000,
            co2_min: 350,
            hum_max: 95,
            hum_min: 0,
            temp_max: 80,
            temp_min: -20,
            tvoc_max: 1000,
            tvoc_min: 0,
            pm10_m_max: 1000,
            pm10_m_min: 0,
            pm25_m_max: 1000,
            pm25_m_min: 0,
            pn03_m_max: 65000,
            pn03_m_min: 0,
            pn10_m_max: 1000,
            pn10_m_min: 0,
            pn25_m_max: 1000,
            pn25_m_min: 0,
            pm100_m_max: 1000,
            pm100_m_min: 0,
            activeConfig: {
              id: 'ashrae',
              mode: 'auto',
              name: 'ASHRAE',
              created: null,
              updated: null,
              createdBy: 'auto',
              thresholds: {
                ci: {
                  max: 500,
                  min: 0,
                  lowerWarning: 0,
                  upperWarning: 50,
                  lowerCritical: 0,
                  upperCritical: 100
                },
                aqi: {
                  max: 500,
                  min: 0,
                  lowerWarning: 0,
                  upperWarning: 50,
                  lowerCritical: 0,
                  upperCritical: 100
                },
                co2: {
                  max: 5000,
                  min: 400,
                  lowerWarning: 400,
                  upperWarning: 800,
                  lowerCritical: 400,
                  upperCritical: 1000
                },
                hum: {
                  max: 100,
                  min: 0,
                  lowerWarning: 30,
                  upperWarning: 60,
                  lowerCritical: 20,
                  upperCritical: 70
                },
                temp: {
                  max: 80,
                  min: -40,
                  lowerWarning: 22,
                  upperWarning: 28,
                  lowerCritical: 18,
                  upperCritical: 30
                },
                tvoc: {
                  max: 1000,
                  min: 0,
                  lowerWarning: 0,
                  upperWarning: 100,
                  lowerCritical: 0,
                  upperCritical: 200
                },
                pm25_m: {
                  max: 1000,
                  min: 0,
                  lowerWarning: 0,
                  upperWarning: 12,
                  lowerCritical: 0,
                  upperCritical: 35
                },
                pn03_m: {
                  max: 65535,
                  min: 0,
                  lowerWarning: 0,
                  upperWarning: 1000,
                  lowerCritical: 0,
                  upperCritical: 2000
                },
                pm100_m: {
                  max: 1000,
                  min: 0,
                  lowerWarning: 0,
                  upperWarning: 35,
                  lowerCritical: 0,
                  upperCritical: 50
                },
                pressure: {
                  max: 692,
                  min: 0,
                  lowerWarning: 395,
                  upperWarning: 415,
                  lowerCritical: 390,
                  upperCritical: 420
                }
              },
              description: 'Ashrae standard 34-2019'
            },
            pressure_max: 600,
            pressure_min: 10,
            rawethanol_max: 5000,
            rawethanol_min: 350,
            ci_threshold_max: 100,
            ci_threshold_min: 0,
            aqi_threshold_max: 100,
            aqi_threshold_min: 0,
            co2_threshold_max: 800,
            co2_threshold_min: 350,
            hum_threshold_max: 60,
            hum_threshold_min: 30,
            temp_threshold_max: 78,
            temp_threshold_min: 65,
            tvoc_threshold_max: 100,
            tvoc_threshold_min: 0,
            userCreatedConfigs: [],
            pm25_m_threshold_max: 12,
            pm25_m_threshold_min: 0,
            pn03_m_threshold_max: 1000,
            pn03_m_threshold_min: 0,
            pm100_m_threshold_max: 35,
            pm100_m_threshold_min: 0,
            pressure_threshold_max: 450,
            pressure_threshold_min: 350
          };
          sessionStorage.setItem('defaultUserConfig', JSON.stringify(defaultUserConfig));

          // Set default filters
          const filterDataTemp = {
            clients: [],
            facility: [],
            zones: [],
            rooms: [],
            sensors: [],
            selectedTime: '24hrs',
            comparisionOption: 'Particle Concentration',
            floorRangeType: 'Temperature',
            timeSeries: ['particleConcentration'],
            timeSeriesType: 'pn03_m',
            detailed: [],
            selectedserial_number: 0
          };
          sessionStorage.setItem('filters', JSON.stringify(filterDataTemp));
          sessionStorage.setItem('timeTrendsFilters', JSON.stringify(filterDataTemp));

          // Fetch data after successful authorization
          const [, devicesList, groupsList] = await Promise.all([
            fetchParameterList(),
            fetchAllDevices(),
            fetchAllGroups()
          ]);

          const { groupId, deviceId } = response.data;
          let resolvedFilter: DashboardDataFilter | null = null;

          if (groupId && Number(groupId) > 0) {
            const numericGroupId = Number(groupId);
            const matchedGroup = groupsList.find((group) => group.groupId === numericGroupId);
            resolvedFilter = {
              type: 'group',
              id: numericGroupId,
              name: matchedGroup?.groupName || `Group ${numericGroupId}`
            };
          } else if (deviceId && Number(deviceId) > 0) {
            const numericDeviceId = Number(deviceId);
            const matchedDeviceFromList = devicesList.find(
              (device) => device.serialNumber === numericDeviceId || device.id === numericDeviceId
            );
            resolvedFilter = {
              type: 'device',
              id: numericDeviceId,
              name: matchedDeviceFromList?.deviceName || `Device ${numericDeviceId}`
            };
          }

          if (resolvedFilter) {
            setDefaultDashboardFilter(resolvedFilter);
            setAppliedFilter({ key: Filters.dashboardDataFilter, data: resolvedFilter });
          } else {
            setDefaultDashboardFilter(null);
            setAppliedFilter({ key: Filters.dashboardDataFilter, data: null });
          }

          setIsAuthorized(true);
          setLoading(false);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        // Clear the token from sessionStorage if validation fails
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('isPublicView');
        setErrorMessage('Public link expired. Please try using a new link.');
        setIsAuthorized(false);
        setLoading(false);
      } finally {
        isValidatingRef.current = false;
      }
    },
    [fetchParameterList, fetchAllDevices, fetchAllGroups]
  );

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setErrorMessage('Public link expired. Please try using a new link.');
      setIsAuthorized(false);
      setLoading(false);
      return;
    }

    validateAndSetupAccess(token);
  }, [searchParams, validateAndSetupAccess]);

  const PublicDataProviderValue = useMemo(
    () => ({
      loading,
      isAuthorized,
      errorMessage,
      deviceDataSummary,
      groupDataSummary,
      parameters,
      dataSummary,
      devices,
      groupedData,
      defaultDashboardFilter,
      fetchDeviceSummary,
      fetchGroupSummary,
      fetchSummary,
      setDataSummary,
      setGroupDataSummary
    }),
    [
      loading,
      isAuthorized,
      errorMessage,
      deviceDataSummary,
      groupDataSummary,
      parameters,
      dataSummary,
      devices,
      groupedData,
      defaultDashboardFilter,
      fetchDeviceSummary,
      fetchGroupSummary,
      fetchSummary
    ]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background-500">
        <div className="text-gray-300 text-lg">Validating access...</div>
      </div>
    );
  }

  if (!isAuthorized || errorMessage) {
    return (
      <div className="flex items-center justify-center h-screen bg-background-500">
        <div className="flex flex-col items-center gap-4">
          <div className="text-gray-300 text-xl font-semibold">Public Link Expired</div>
          <div className="text-gray-400 text-base">Please try using a new link.</div>
        </div>
      </div>
    );
  }

  return (
    <PublicDataContext.Provider value={PublicDataProviderValue}>
      <Outlet />
    </PublicDataContext.Provider>
  );
}

export const usePublicData = (): PublicDataContextProps => useContext(PublicDataContext);
